import { z } from "zod"
import { OrderDirectionEnum, PaginationMetadataSchema, PaginationMetadataType } from "@tutellus/core-contracts-ifaces"

const PaginationValidation = z.object({
  offset: z.number({ required_error: "offset is required" }),
  limit: z.number({ required_error: "limit is required" }),
  orderBy: z.string({ required_error: "orderBy is required" }),
  orderDirection: z.nativeEnum(OrderDirectionEnum),
  numElements: z.number({ required_error: "numElements is required" }).optional(),
})

export type PaginationSerialized = ReturnType<Pagination["serialize"]>
export class Pagination {
  constructor(
    private readonly _offset: number,
    private readonly _limit: number,
    private readonly _orderBy: string,
    private readonly _orderDirection: OrderDirectionEnum,
    private readonly _numElements?: number,
  ) {}

  static create({ offset, limit, orderBy, orderDirection, numElements }: z.infer<typeof PaginationValidation>) {
    const { success, error } = PaginationValidation.safeParse({ offset, limit, orderBy, orderDirection, numElements })
    if (!success) throw new Error(error.message)
    return new Pagination(offset, limit, orderBy, orderDirection, numElements)
  }

  static createFromAPI({ offset, limit, orderBy, orderDirection, numElements }: PaginationMetadataType) {
    const { success, error } = PaginationMetadataSchema.safeParse({
      offset,
      limit,
      orderBy,
      orderDirection,
      numElements,
    })
    if (!success) throw new Error(error.message)
    return new Pagination(offset, limit, orderBy, orderDirection, numElements ?? undefined)
  }

  static createDefault({ orderBy }: { orderBy: string }) {
    return new Pagination(0, 20, orderBy, OrderDirectionEnum.DESC)
  }

  get offset() { return this._offset } // prettier-ignore
  get limit() { return this._limit } // prettier-ignore
  get orderBy() { return this._orderBy } // prettier-ignore
  get orderDirection() { return this._orderDirection } // prettier-ignore
  get numElements() { return this._numElements } // prettier-ignore
  get currentPage() { return Math.floor(this.offset / this.limit) + 1 } // prettier-ignore

  updateCurrentPage(newPage: number) {
    return new Pagination((newPage - 1) * this.limit, this.limit, this.orderBy, this.orderDirection, this.numElements)
  }

  shouldShowPagination(): boolean {
    return this.numElements !== undefined && this.numElements > this.limit
  }

  metadata() {
    return {
      offset: this.offset,
      limit: this.limit,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
    }
  }

  serialize() {
    return {
      ...this.metadata(),
      numElements: this.numElements ?? undefined,
    }
  }
}
